import { Component, OnInit } from '@angular/core';
import { AlertController, MenuController, ModalController } from '@ionic/angular';
import { LoginRequiredSheetComponent } from 'src/app/login-required-sheet/login-required-sheet.component';
import { ApiService } from 'src/app/services/api.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { jhcassoAPIUri } from 'src/environments/apiUri';
import { environment } from 'src/environments/environment';
import { ProfileService } from 'src/app/services/profile.service';
import { ProfileData } from 'src/app/models/Response/ProfileResponse';
import { Share } from '@capacitor/share';
import { NavigationService } from 'src/app/services/navigation.service';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit {

  isLoggedIn = false;
  userProfile: ProfileData | null = null;
  appVersion = '1.0.0';

  constructor(
    private api: ApiService,
    private menuCtrl: MenuController,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private authenticationService: AuthenticationService,
    private profileService: ProfileService,
    private nav: NavigationService
  ) { }

  ngOnInit() {

    this.appVersion = environment.appVersion;
    this.isLoggedIn = this.api.isLoggedIn();

    // Load cached profile instantly
    this.userProfile = this.profileService.getProfile();

    // Reactive profile updates
    this.profileService.profile$.subscribe(profile => {
      this.userProfile = profile;
    });

  }

  onImageError(event: any) {
    event.target.src = 'assets/profile-placeholder.png';
  }

  // ================= PROFILE =================

  async goToProfile() {
    this.menuCtrl.close();
    this.checkUserisLoggedIn(() => this.nav.profile());
  }

  async login() {
    this.menuCtrl.close();
    this.nav.login();
  }

  async Logout() {

    const alert = await this.alertCtrl.create({
      header: "Confirm Logout",
      message: "Are you sure you want to logout?",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
          cssClass: "secondary",
        },
        {
          text: "Logout",
          handler: () => {

            this.api
              .post(jhcassoAPIUri.logout, null)
              .subscribe(() => {
                this.authenticationService.logout();
              });

          },
        },
      ],
    });

    await alert.present();
  }

  // ================= LOCATION =================

  async setLocation() {
    await this.menuCtrl.close();
    this.nav.setLocation();
  }

  deleteAccount() {
    // Future feature
  }

  // ================= BOOKINGS =================

  async myBookings() {
    await this.menuCtrl.close();
    await this.checkUserisLoggedIn(() => this.nav.appointment());
  }

  async myWallet() {
    await this.menuCtrl.close();
    await this.checkUserisLoggedIn(() => this.nav.wallet());
  }

  async savedAddresses() {
    await this.menuCtrl.close();
    this.nav.addAddress();
  }

  async notifications() {
    await this.menuCtrl.close();
    await this.checkUserisLoggedIn(() => this.nav.notification());
  }

  // ================= OFFERS =================

  async topOffers() {
    await this.menuCtrl.close();
    this.nav.offers();
  }

  async referEarn() {
    await this.menuCtrl.close();
    await this.checkUserisLoggedIn(() => this.nav.referAndEarn());
  }

  async inviteFriend() {

    this.menuCtrl.close();

    this.checkUserisLoggedIn(async () => {

      const shareUrl = `https://user.jhacasso.com/login-signup?ref=${this.userProfile?.referralCode}`;

      const message = `Hey 👋
I just found an amazing salon booking app — Jhacasso 💇✨
Use my code *${this.userProfile?.referralCode}* and get ₹100 instantly on signup 🎉
Limited-time reward — Don’t miss it!`;

      try {

        await Share.share({
          title: 'Get ₹100 Bonus on Jhacasso!',
          text: message,
          url: shareUrl,
          dialogTitle: 'Invite your friends'
        });

      } catch (error) {

        console.error('Share failed:', error);

      }

    });

  }

  async blog() {
    await this.menuCtrl.close();
    this.nav.blog();
  }

  // ================= SETTINGS =================

  async appSettings() {
    await this.menuCtrl.close();
    this.nav.appSettings();
  }

  async locationSettings() {
    await this.menuCtrl.close();
    this.nav.locationSettings();
  }

  // ================= SUPPORT =================

  async contactUs() {
    await this.menuCtrl.close();
    this.nav.contactUs();
  }

  async submitRequest() {
    await this.menuCtrl.close();
    this.nav.submitRequest();
  }

  async supportTickets() {
    await this.menuCtrl.close();
    this.nav.supportTickets();
  }

  // ================= LEGAL =================

  async termsConditions() {
    await this.menuCtrl.close();
    this.nav.terms();
  }

  async refundPolicy() {
    await this.menuCtrl.close();
    this.nav.refundPolicy();
  }

  async privacypolicy() {
    await this.menuCtrl.close();
    this.nav.privacyPolicy();
  }

  // ================= LOGIN CHECK =================

  async checkUserisLoggedIn(callback: () => void) {

    if (!this.api.isLoggedIn()) {
      this.openLoginRequiredModal();
      return;
    }

    callback();
  }

  async openLoginRequiredModal() {

    await this.menuCtrl.close();

    const modal = await this.modalCtrl.create({
      component: LoginRequiredSheetComponent,
      cssClass: 'login-required-modal',
      backdropDismiss: true
    });

    await modal.present();

  }

}